import type { Acao, Recurso } from '@locaveri/shared'

/**
 * O vocabulário técnico do RBAC, em português — só para a TELA.
 *
 * `RECURSOS` e `ACOES` são inglês no singular de propósito (CLAUDE.md): eles
 * viram string em código de painel, de API e de app, e precisam ser estáveis.
 * Ninguém renomeia `vehicle_category` porque o negócio passou a dizer "grupo".
 *
 * Mas o gestor que abre a tela de permissões não é desenvolvedor. Marcar
 * `service_provider.update` sem saber que aquilo é "motorista e segurança" é
 * como se concede acesso por engano — e permissão concedida por engano só
 * aparece no dia em que alguém usa.
 *
 * Estes mapas moram no painel, e não no pacote compartilhado, porque são
 * APRESENTAÇÃO de uma tela que só existe aqui: o app do cliente nunca lista
 * recurso de RBAC.
 */

export const ROTULO_DO_RECURSO: Readonly<Record<Recurso, string>> = {
  tenant: 'Configuração da empresa',
  account: 'Contas de cliente',
  user: 'Usuários',
  role: 'Perfis de acesso',
  permission: 'Catálogo de permissões',
  audit_log: 'Trilha de auditoria',
  vehicle: 'Veículos',
  vehicle_category: 'Categorias',
  location: 'Pontos de retirada',
  vehicle_cost: 'Custos do veículo',
  maintenance_plan: 'Plano de manutenção',
  cost_policy: 'Política de custo',
  inspection: 'Vistorias',
  price_table: 'Tabelas de preço',
  extra: 'Opcionais',
  coupon: 'Cupons',
  quote: 'Cotações',
  service_provider: 'Motoristas e segurança',
  waitlist: 'Lista de espera',
  rental_policy: 'Política de locação',
  overbooking: 'Overbooking',
  availability: 'Disponibilidade',
  reservation: 'Reservas',
  allocation: 'Alocação de veículo',
  contract: 'Contratos',
  settlement: 'Fechamento da locação',
  incident: 'Sinistros',
  traffic_fine: 'Multas',
  telemetry: 'Rastreamento',
  invoice: 'Faturas',
  payment: 'Pagamentos',
  report: 'Relatórios',
  dashboard: 'Painéis',
  survey: 'Pesquisa de satisfação',
  storefront: 'Site público',
  ai_insight: 'Insights de IA',
  fleet_occupancy: 'Ocupação da frota (sem valores)',
  credit_analysis: 'Análise cadastral (Mira Score)',
}

/**
 * As nove ações.
 *
 * `quote` e `adopt` não são CRUD e por isso têm nome próprio no catálogo —
 * "cotar" no site não é criar cotação do painel, e "adotar" é a transferência
 * de posse de uma cotação anônima para a conta recém-criada. Os rótulos
 * preservam essa distinção em vez de espremê-las em "criar" e "editar".
 *
 * `cost` é a única que não abre uma ROTA e sim um CAMPO — o custo de diária do
 * prestador dentro de `GET /providers`. O rótulo diz "Ver o custo" e não "Ver"
 * justamente porque, na matriz, ela aparece na MESMA linha de
 * `service_provider.read`: dois "Ver" lado a lado não diriam qual é qual.
 *
 * ⚠️ Este `Record` é exaustivo e foi ele que cobrou o rótulo novo — `Acao`
 * ganhou um membro e o `tsc` reprovou a tela antes de alguém ver a matriz com
 * uma coluna em branco.
 */
export const ROTULO_DA_ACAO: Readonly<Record<Acao, string>> = {
  create: 'Criar',
  read: 'Ver',
  update: 'Editar',
  delete: 'Excluir',
  approve: 'Aprovar',
  export: 'Exportar',
  quote: 'Cotar',
  adopt: 'Adotar',
  cost: 'Ver o custo',
}

/**
 * Recursos que o gestor lê como "administração do sistema", separados do resto.
 *
 * A separação é de LEITURA, não de segurança: numa lista de 34 recursos em
 * ordem alfabética, "Perfis de acesso" cai entre "Pagamentos" e "Pontos de
 * retirada", e a caixa mais perigosa da tela fica indistinguível das outras.
 */
export const RECURSOS_DE_ADMINISTRACAO: ReadonlySet<Recurso> = new Set<Recurso>([
  'tenant',
  'role',
  'permission',
  'audit_log',
  'user',
])
